import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const AddBank = () => {
  const navigate = useNavigate();
  const { paymentType } = useParams(); // Get payment type from URL
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: "",
    account_number: "",
    bank_name: "",
    ifsc_code: "",
    upi_id: "",
    qr_img: null,
    isactive: true,
  });
  const [qrPreview, setQrPreview] = useState(null);
  const [existingData, setExistingData] = useState(null);

  const METHOD_NAMES = {
    bkash: "Bkash",
    nagad: "Nagad",
    rocket: "Rocket",
    upay: "Upay"
  };

  useEffect(() => {
    if (paymentType) {
      fetchExistingData();
    }
  }, [paymentType]);

  const fetchExistingData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API}/admin/getBankDetails`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const existing = response.data?.data?.find(
        (item) => item.payment_type === paymentType
      );

      if (existing) {
        setExistingData(existing);
        setFormData({
          account_holder_name: existing.account_holder_name || "",
          account_number: existing.account_number || "",
          bank_name: existing.bank_name || "",
          ifsc_code: existing.ifsc_code || "",
          upi_id: existing.upi_id || "",
          qr_img: null,
          isactive: existing.isactive || false,
        });

        if (existing.qr_img) {
          setQrPreview(
            existing.qr_img.startsWith("data:image")
              ? existing.qr_img
              : `${process.env.REACT_APP_BASE_URL_API}/uploads/QrCodes/${existing.qr_img}`
          );
        }
      }
    } catch (err) {
      console.error("Failed to fetch existing data:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;
        setFormData({ ...formData, qr_img: base64Image });
        setQrPreview(base64Image);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        payment_type: paymentType,
        account_holder_name: formData.account_holder_name,
        account_number: formData.account_number,
        bank_name: formData.bank_name,
        ifsc_code: formData.ifsc_code,
        upi_id: formData.upi_id,
        isactive: formData.isactive,
      };

      // Only include qr_img if a new image was uploaded
      if (formData.qr_img && typeof formData.qr_img === 'string' && formData.qr_img.startsWith('data:image')) {
        dataToSend.qr_img = formData.qr_img;
      }

      console.log("Submitting data:", dataToSend);
      console.log("Existing data:", existingData);

      let response;
      if (existingData) {
        // Update existing record
        console.log(`Updating record ID: ${existingData.id}`);
        response = await axios.post(
          `${process.env.REACT_APP_BASE_URL_API}/admin/updateBankDetailsSettingsById/${existingData.id}`,
          dataToSend,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Update response:", response.data);
      } else {
        // Create new record
        console.log("Creating new record");
        response = await axios.post(
          `${process.env.REACT_APP_BASE_URL_API}/admin/addBankDetailsSettings`,
          dataToSend,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Create response:", response.data);
      }

      toast.success(
        existingData
          ? "Payment method updated successfully!"
          : "Payment method created successfully!"
      );
      navigate("/banking");
    } catch (err) {
      console.error("Failed to save payment method:", err);
      console.error("Error response:", err.response?.data);
      toast.error(err.response?.data?.message || "Failed to save payment method. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <main className="flex ml-0 lg:ml-[230px] overflow-auto bg-stone-100 pb-8">
      <div className="w-full flex-1 px-5">
        <div className="my-5">
          <div className="flex items-center justify-between">
            <div className="w-auto">
              <h1 className="font mb-3 flex flex-wrap items-center text-lg font-bold text-stone-800 md:text-xl">
                {existingData ? "Update" : "Configure"} {METHOD_NAMES[paymentType]} Details
              </h1>
              <p className="text-sm text-gray-600">
                {existingData
                  ? "Update payment details and QR code"
                  : "Add payment details and QR code for this method"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a className="font-semibold text-cm-gray" href="/banking">
              Payment Methods<span className="ml-2">/</span>
            </a>
            <a className="font-semibold text-cm-gray" href="#">
              {existingData ? "Update" : "Configure"} {METHOD_NAMES[paymentType]}
            </a>
          </div>
        </div>

        <section className="overflow-auto">
          <form onSubmit={handleSubmit} className="shadow-lg relative rounded-lg bg-white py-1">
            <div className="grid gap-5 rounded-lg p-5 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="account_holder_name">
                  Account Holder Name *
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="account_holder_name"
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="account_number">
                  Account Number / Phone Number *
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="account_number"
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="bank_name">
                  Bank/Provider Name
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="bank_name"
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="ifsc_code">
                  IFSC Code / Reference Code
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="ifsc_code"
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="upi_id">
                  UPI ID / Payment ID *
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="upi_id"
                  type="text"
                  name="upi_id"
                  value={formData.upi_id}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* <div>
                <label className="block text-sm font-medium mb-2" htmlFor="min_amount">
                  Minimum Amount *
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="min_amount"
                  type="number"
                  name="min_amount"
                  value={formData.min_amount}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="max_amount">
                  Maximum Amount *
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="max_amount"
                  type="number"
                  name="max_amount"
                  value={formData.max_amount}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div> */}

              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="qr_img">
                  QR Code Image {existingData ? "(Optional - leave empty to keep current)" : "*"}
                </label>
                <input
                  className="rounded-lg py-2 w-full outline-none border px-3"
                  id="qr_img"
                  type="file"
                  name="qr_img"
                  accept="image/*"
                  onChange={handleFileChange}
                  required={!existingData}
                />
                {qrPreview && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Preview:</p>
                    <img
                      src={qrPreview}
                      alt="QR Code Preview"
                      className="w-32 h-32 object-contain border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </div>
              {/* 
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isactive"
                    checked={formData.isactive}
                    onChange={handleInputChange}
                    className="mr-2 w-5 h-5"
                  />
                  <span className="text-sm font-medium">Active (Enable this payment method)</span>
                </label>
              </div> */}
            </div>

            <div className="my-5 flex items-center justify-center gap-4">
              <Link
                to="/banking"
                className="flex items-center gap-1 px-5 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                <svg
                  stroke="currentColor"
                  fill="none"
                  strokeWidth={0}
                  viewBox="0 0 15 15"
                  height="1em"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.85355 3.14645C7.04882 3.34171 7.04882 3.65829 6.85355 3.85355L3.70711 7H12.5C12.7761 7 13 7.22386 13 7.5C13 7.77614 12.7761 8 12.5 8H3.70711L6.85355 11.1464C7.04882 11.3417 7.04882 11.6583 6.85355 11.8536C6.65829 12.0488 6.34171 12.0488 6.14645 11.8536L2.14645 7.85355C1.95118 7.65829 1.95118 7.34171 2.14645 7.14645L6.14645 3.14645C6.34171 2.95118 6.65829 2.95118 6.85355 3.14645Z"
                    fill="currentColor"
                  />
                </svg>
                <span>Cancel</span>
              </Link>
              <button
                className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                type="submit"
                disabled={loading}
              >
                {loading ? "Saving..." : existingData ? "Update Details" : "Save Details"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default AddBank;
